import { authClient } from "@features/auth/authClient";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Suspense, useState, useEffect } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { toast } from "sonner";
import { PageContainer } from "~/components/layout/PageContainer";
import { AppHeader } from "~/components/layout/AppHeader";
import { RaffleCard } from "~/assets/raffle/RaffleCard";
import { RafflePresent } from "~/assets/raffle/RafflePresent";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import type { playQuery } from "./__generated__/playQuery.graphql";
import type { playRaffleAttemptMutation } from "./__generated__/playRaffleAttemptMutation.graphql";
import type { playRaffleFetchConsolationMutation } from "./__generated__/playRaffleFetchConsolationMutation.graphql";

const query = graphql`
	query playQuery($hubName: String!, $raffleId: UUID!) {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
				slug
				meta @required(action: THROW) {
					id
					name
					street
				}
				raffle {
					reservations {
						id
						usecase
						space {
							id
							position
							frame
						}
					}
				}
			}
		}
		raffle(id: $raffleId) @required(action: THROW) {
			id
			status
			raffleAttempts {
				id
				reservation {
					id
					usecase
				}
			}
		}
	}
`;

const raffleAttemptMutation = graphql`
	mutation playRaffleAttemptMutation($raffleId: UUID!, $reservationId: UUID!) {
		raffleAttemptCreate(input: { raffleId: $raffleId, reservationId: $reservationId }) {
			id
			raffle {
				id
				status
			}
			unlockRequest {
				id
				status
			}
		}
	}
`;

const raffleFetchConsolationMutation = graphql`
	mutation playRaffleFetchConsolationMutation($raffleId: UUID!) {
		raffleFetchConsolation(input: { raffleId: $raffleId }) {
			id
			status
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/raffle/$raffleId/play")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	loader: async ({ params, context }) => {
		try {
			return preloadQuery<playQuery>(context.relayEnvironment, query, {
				hubName: params.hubMetaName,
				raffleId: params.raffleId,
			});
		} catch (error) {
			throw redirect({
				to: "/$hubMetaName/raffle",
				params: { hubMetaName: params.hubMetaName },
			});
		}
	},
});

type Card = {
	id: string;
	isWinner: boolean;
	isFlipped: boolean;
	reservation: {
		id: string;
		usecase: string;
	};
};

type RaffleState =
	| { type: "idle" }
	| { type: "playing"; cards: Card[]; attemptsLeft: number }
	| { type: "winner"; unlockRequestId: string }
	| { type: "loser"; canGetConsolation: boolean; cards: Card[] }
	| { type: "fetchingConsolation"; cards: Card[] }
	| { type: "navigatingToResult"; winner: boolean; unlockRequestId: string };

function RaffleGame() {
	const data = usePreloadedQuery<playQuery>(query, Route.useLoaderData());
	const navigate = useNavigate();
	const { hubMetaName } = Route.useParams();
	const [makeAttempt, isAttempting] = useAsyncMutation<playRaffleAttemptMutation>(raffleAttemptMutation);
	const [fetchConsolation, isFetchingConsolation] = useAsyncMutation<playRaffleFetchConsolationMutation>(raffleFetchConsolationMutation);
	const i18n = useI18nContext().LL["new-ui"];

	const queryData = data;
	const { hubMeta, raffle } = queryData;
	const userRaffle = raffle;
	const hub = hubMeta.hub;
	const raffleSpaces = hub.raffle?.reservations || [];

	const [raffleState, setRaffleState] = useState<RaffleState>(() => {
		if (!userRaffle || raffleSpaces.length === 0) {
			return { type: "idle" };
		}

		const attemptedMap = new Map(userRaffle.raffleAttempts.map((attempt) => [attempt.reservation.id, attempt.reservation.usecase]));
		const attemptedEntries = [...attemptedMap.entries()];

		const activePrizeSpaces = raffleSpaces.filter((space) => space.usecase === "PRIZE");
		const activeEmptySpaces = raffleSpaces.filter((space) => space.usecase === "EMPTY");
		const targetEmptyCount = Math.min(5, activeEmptySpaces.length);

		if (activePrizeSpaces.length === 0) {
			return { type: "idle" };
		}

		const attemptedPrizeSpace = attemptedEntries
			.filter(([, usecase]) => usecase === "PRIZE")
			.map(([reservationId]) => activePrizeSpaces.find((space) => space.id === reservationId))
			.find((space) => Boolean(space));

		const selectedPrizeSpace = attemptedPrizeSpace ?? activePrizeSpaces[Math.floor(Math.random() * activePrizeSpaces.length)];
		if (!selectedPrizeSpace) {
			return { type: "idle" };
		}

		const attemptedEmptySpaces = attemptedEntries
			.filter(([, usecase]) => usecase === "EMPTY")
			.map(([reservationId]) => activeEmptySpaces.find((space) => space.id === reservationId))
			.filter((space): space is (typeof activeEmptySpaces)[number] => Boolean(space));

		const uniqueAttemptedEmptySpaces = attemptedEmptySpaces.filter(
			(space, index, arr) => arr.findIndex((candidate) => candidate.id === space.id) === index
		);

		const selectedAttemptedEmptySpaces = uniqueAttemptedEmptySpaces.slice(0, targetEmptyCount);

		const remainingEmptyCandidates = activeEmptySpaces.filter(
			(space) => !selectedAttemptedEmptySpaces.some((attemptedSpace) => attemptedSpace.id === space.id)
		);

		const shuffledRemainingEmptyCandidates = [...remainingEmptyCandidates].sort(() => Math.random() - 0.5);
		const neededAdditionalEmpties = targetEmptyCount - selectedAttemptedEmptySpaces.length;

		const selectedEmptySpaces = [...selectedAttemptedEmptySpaces, ...shuffledRemainingEmptyCandidates.slice(0, neededAdditionalEmpties)];

		const selectedSpaces = [selectedPrizeSpace, ...selectedEmptySpaces];
		if (selectedSpaces.length < 1 + targetEmptyCount) {
			return { type: "idle" };
		}

		// Build a fixed deck: exactly 1 PRIZE + up to 5 EMPTY
		const initialCards: Card[] = selectedSpaces.map((space) => ({
			id: space.id,
			isWinner: space.usecase === "PRIZE",
			isFlipped: attemptedMap.has(space.id),
			reservation: {
				id: space.id,
				usecase: attemptedMap.get(space.id) || space.usecase,
			},
		}));

		// Shuffle cards to randomly position the winning card
		const shuffledCards = [...initialCards].sort(() => Math.random() - 0.5);
		const attemptsLeft = 2 - userRaffle.raffleAttempts.length;

		// Check if user is LOSER with no attempts left
		if (userRaffle.status === "LOSER" && attemptsLeft === 0) {
			return { type: "loser", canGetConsolation: true, cards: shuffledCards };
		}

		return { type: "playing", cards: shuffledCards, attemptsLeft };
	});

	useEffect(() => {
		if (raffleState.type !== "idle") {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			navigate({
				to: "/$hubMetaName/raffle",
				params: { hubMetaName },
				replace: true,
			});
		}, 1200);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [hubMetaName, navigate, raffleState.type]);

	const handleGetSurprise = async () => {
		if (!userRaffle || raffleState.type !== "loser") return;

		const currentCards = raffleState.cards;
		setRaffleState({ type: "fetchingConsolation", cards: currentCards });

		try {
			const result = await fetchConsolation({
				variables: {
					raffleId: userRaffle.id,
				},
			});

			const unlockRequestId = result.raffleFetchConsolation.id;

			// Navigate to result page with the consolation unlock request
			navigate({
				from: Route.fullPath,
				to: "../result",
				search: { winner: false, unlockRequestId },
			});
		} catch (error) {
			toast.error(i18n.RAFFLE_GAME_FAILED_GET_SURPRISE());
			setRaffleState({ type: "loser", canGetConsolation: true, cards: currentCards });
		}
	};

	const handleCardClick = async (cardIndex: number) => {
		if (raffleState.type !== "playing" || !userRaffle || userRaffle.status !== "ONGOING") {
			return;
		}

		const clickedCard = raffleState.cards[cardIndex];
		if (!clickedCard || clickedCard.isFlipped) {
			return;
		}

		// Update the card to show it's flipped
		const newCards = [...raffleState.cards];
		newCards[cardIndex] = { ...clickedCard, isFlipped: true };
		setRaffleState({ ...raffleState, cards: newCards });

		try {
			const result = await makeAttempt({
				variables: {
					raffleId: userRaffle.id,
					reservationId: clickedCard.id,
				},
			});

			const newStatus = result.raffleAttemptCreate.raffle.status;
			const unlockRequestId = result.raffleAttemptCreate.unlockRequest.id;
			const newAttemptsLeft = raffleState.attemptsLeft - 1;

			if (newStatus === "WINNER") {
				// Keep showing the game state, navigate directly
				setTimeout(() => {
					navigate({
						from: Route.fullPath,
						to: "../result",
						search: { winner: true, unlockRequestId },
					});
				}, 500);
			} else if (newStatus === "LOSER") {
				// Show loser state with option to get surprise
				setRaffleState({ type: "loser", canGetConsolation: true, cards: newCards });
			} else {
				// Still playing
				setRaffleState({ type: "playing", cards: newCards, attemptsLeft: newAttemptsLeft });
			}
		} catch (error) {
			toast.error(i18n.RAFFLE_GAME_FAILED_MAKE_ATTEMPT());
			// Revert the card flip on error
			setRaffleState({ ...raffleState });
		}
	};

	const shouldShowButton = (raffleState.type === "loser" && raffleState.canGetConsolation) || raffleState.type === "fetchingConsolation";

	return (
		<PageContainer
			footer={
				shouldShowButton ? (
					<Button className="w-full shadow-xl" size="lg" onClick={handleGetSurprise} disabled={raffleState.type === "fetchingConsolation"}>
						{raffleState.type === "fetchingConsolation" ? (
							<T>{i18n.RAFFLE_GAME_GETTING_SURPRISE()}</T>
						) : (
							<T>{i18n.RAFFLE_GAME_GET_SURPRISE()}</T>
						)}
					</Button>
				) : undefined
			}
		>
			<AppHeader location={{ address: hub.meta.street }} service="RAFFLE" />
			<div className="text-center py-8 px-6">
				<h1 className="text-2xl font-bold mb-2">
					<T>{i18n.RAFFLE_GAME_TITLE()}</T>
				</h1>
				{raffleState.type === "playing" && raffleState.attemptsLeft === 2 && (
					<p className="text-base text-gray-600">
						<T>{i18n.RAFFLE_GAME_INSTRUCTION()}</T>
					</p>
				)}
			</div>

			{raffleState.type === "idle" && (
				<p className="text-center text-base mb-4 px-6">
					<T>{i18n.RAFFLE_GAME_LOADING()}</T>
				</p>
			)}

			{raffleState.type === "playing" && raffleState.attemptsLeft < 2 && (
				<p className="text-center text-base mb-4 px-6">
					{raffleState.attemptsLeft === 1 && <T>{i18n.RAFFLE_GAME_ONE_TRY_LEFT()}</T>}
					{raffleState.attemptsLeft === 0 && <T>{i18n.RAFFLE_GAME_NO_TRIES_LEFT()}</T>}
				</p>
			)}

			{raffleState.type === "loser" && raffleState.canGetConsolation && (
				<p className="text-center text-base mb-4 px-6">
					<T>{i18n.RAFFLE_GAME_BOTH_ATTEMPTS_USED()}</T>
				</p>
			)}

			{(raffleState.type === "playing" || raffleState.type === "loser" || raffleState.type === "fetchingConsolation") && (
				<div className="pb-8 px-6">
					<div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
						{raffleState.cards.map((card, index) => (
							<div key={`card-${index}`}>
								<button
									onClick={() => handleCardClick(index)}
									disabled={card.isFlipped || isAttempting || raffleState.type === "loser" || raffleState.type === "fetchingConsolation"}
									className={`relative w-full aspect-square rounded-2xl transition-all duration-300 disabled:cursor-not-allowed shadow-lg ${
										card.isFlipped ? (card.reservation.usecase === "PRIZE" ? "bg-green-200" : "bg-white") : ""
									}`}
								>
									{!card.isFlipped ? (
										<div className="w-full h-full rounded-2xl flex items-center justify-center p-4">
											<RaffleCard className="w-full h-full object-contain" />
										</div>
									) : (
										<div className="w-full h-full rounded-2xl flex items-center justify-center p-4">
											{card.reservation.usecase === "PRIZE" ? (
												<RafflePresent className="w-16 h-16" />
											) : (
												<div className="text-center flex flex-col items-center justify-center">
													<p className="text-xl font-bold text-black">
														<T>{i18n.RAFFLE_GAME_NO_GIFT()}</T>
													</p>
												</div>
											)}
										</div>
									)}
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</PageContainer>
	);
}

function RouteComponent() {
	return (
		<Suspense fallback={<div>Loading...</div>}>
			<RaffleGame />
		</Suspense>
	);
}
